import * as React from "react";
import { lazy, Suspense } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, FlaskConical, Maximize2, Pencil } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/stores/auth.store";
import { useStartSandbox } from "@/features/simulations/sandbox/hooks/useStartSandbox";
import type { LineSummary, MachineDetail } from "@/types/lines.types";

const HOVER_ICON =
  "grid h-[30px] w-[30px] place-items-center rounded-md border border-border-subtle bg-bg-surface text-text-tertiary transition-colors hover:bg-bg-muted-rd hover:text-text-primary focus-visible:outline-none focus-visible:[box-shadow:var(--rd-shadow-focus)]";

const CanvasFullscreenModal = lazy(() =>
  import("@/features/lines/canvas/CanvasFullscreenModal").then((m) => ({
    default: m.CanvasFullscreenModal,
  }))
);

interface Props {
  line: LineSummary;
  onMachineClick: (m: MachineDetail) => void;
}

export function LineCardActions({ line, onMachineClick }: Props): React.ReactElement {
  const navigate = useNavigate();
  const role = useAuthStore((s) => s.user?.role);
  const startSandbox = useStartSandbox();
  const [fullscreen, setFullscreen] = React.useState(false);

  return (
    <div className="flex shrink-0 items-center gap-1">
      <button
        type="button"
        className={cn(HOVER_ICON)}
        aria-label={`Tela cheia ${line.name}`}
        onClick={() => setFullscreen(true)}
      >
        <Maximize2 className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
      <button
        type="button"
        className={cn(HOVER_ICON)}
        aria-label={`Ver detalhes ${line.name}`}
        onClick={() => navigate(`/lines/${line.id}/detail`)}
      >
        <Eye className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
      {role === "GESTOR" ? (
        <>
          <button
            type="button"
            className={cn(HOVER_ICON)}
            aria-label={`Editar ${line.name}`}
            onClick={() => navigate(`/lines/${line.id}/detail`)}
          >
            <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
          <button
            type="button"
            className={cn(HOVER_ICON)}
            aria-label={`Simular ${line.name}`}
            onClick={() => void startSandbox(line.id, line.name)}
          >
            <FlaskConical className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        </>
      ) : null}

      <Suspense fallback={null}>
        <CanvasFullscreenModal
          open={fullscreen}
          onClose={() => setFullscreen(false)}
          lineId={line.id}
          lineName={line.name}
          onMachineClick={onMachineClick}
        />
      </Suspense>
    </div>
  );
}
